/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useContext } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import Popup from '../../components/popups/popup';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import { formatPrice } from '../../utils';
import DriverPaymentService from '../../services/driverPaymentService';
import DriverDropdown from '../../components/dropdowns/driverDropdown';
import UserDropdown from '../../components/dropdowns/userDropdown';
import TimeDropdown from '../../components/dropdowns/timeDropdown';
import DeleteDriverPaymentModal from '../../components/modals/driverPayment/deleteDriverPaymentModal';
import ViewDriverPaymentModal from '../../components/modals/driverPayment/viewDriverPaymentModal';
import PdfOneDriverPayment from '../../components/prints/pdfs/driverPayment/pdfOneDriverPayment';
import BranchDropdown from '../../components/dropdowns/branchDropdown';
import { AuthContext } from '../../provider/authProvider';
import PdfDriverPaymentList from '../../components/prints/pdfs/driverPayment/pdfDriverPaymentList';
import { Link } from 'react-router-dom';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';

const DriverPayments = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [driverPayments, setDriverPayments] = useState([]);
    const [selectedDriverPayment, setSelectedDriverPayment] = useState({});
    const [totalPrice, setTotalPrice] = useState(0);

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedTime, setSelectedTime] = useState({
        time: 'today',
        value: t('today')
    });

    const [openDelete, setOpenDelete] = useState(false);
    const [openView, setOpenView] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (driverPayment) => {
        setSelectedDriverPayment(driverPayment);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);


    const handleOpenDelete = (driverPayment) => {
        setSelectedDriverPayment(driverPayment);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSelectedUser(null);
            setSelectedDriver(null);
            await fetchDriverPayments();
        }
    };

    const fetchDriverPayments = async (page = 1, branchId = null, driverId = null, userId = null) => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                branchId, driverId, userId,
                ...sortState,
                ...selectedTime
            };
            const data = await DriverPaymentService.fetch(page, filters);
            const { payments, driverPaymentsAmount } = data;

            setTotalPrice(driverPaymentsAmount);

            if (payments.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setDriverPayments([]);
                }
            } else {
                setDriverPayments((prevs) => (page === 1 ? payments : [...prevs, ...payments]));
            }
        } catch (error) {
            console.error('Error fetching driver payments:', error);
        } finally {
            setIsLoading(false);
        }
    };


    const resetFilter = () => {
        setSelectedUser(null);
        setSelectedDriver(null);
        setSelectedBranch(null);
        setHasMore(true)
        setSelectedTime({
            time: 'today',
            value: t('today')
        });
        if (page !== 1) setPage(1);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            fetchDriverPayments(1, selectedBranch?.id, selectedDriver?.id, selectedUser?.id);
            return;
        }
    }, []);

    useEffect(() => {
        if (page > 1) {
            fetchDriverPayments(page, selectedBranch?.id, selectedDriver?.id, selectedUser?.id);
        }
    }, [page]);

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        fetchDriverPayments(1, selectedBranch?.id, selectedDriver?.id, selectedUser?.id);
    }, [selectedBranch, selectedUser, selectedDriver, sortState, selectedTime]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchDriverPayments(page + 1, selectedBranch?.id, selectedDriver?.id, selectedUser?.id);
                setPage(prevPage => prevPage + 1);
            }
        }
    };


    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
            width: '100%',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteDriverPaymentModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} driverPayment={selectedDriverPayment} />
                <ViewDriverPaymentModal open={openView} handleClose={handleCloseView} driverPaymentId={selectedDriverPayment.id} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {/* Totals row */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'end'
            }}>
                <Box
                    sx={{
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        height: 60,
                        width: isTablet ? '90%' : '30%'
                    }}
                >
                    <Box width={isTablet ? '80%' : '75%'}>
                        <Typography sx={{ mx: isTablet ? 0.4 : 1, fontSize: isTablet ? 14 : 16 }}>
                            {t('totalPriceAmount')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalPrice)}</span>
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: isTablet ? '20%' : '25%'
                        }}>
                        <PdfDriverPaymentList
                            data={{
                                branch: selectedBranch,
                                user: selectedUser,
                                driver: selectedDriver,
                                time: selectedTime,
                            }}
                        />
                    </Box>
                </Box>
            </Box>


            {/* Filter row */}
            {
                isTablet ? <Box
                    sx={{
                        mb: 2,
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,

                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation='vertical' />
                        <Box sx={{ alignContent: 'center', height: 50, width: '95%' }}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                    </Box>
                    {allBranchPermission && <Box>
                        <Divider />
                        <Box sx={{
                            alignContent: 'center',
                            height: 50,
                            width: '100%'
                        }}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>
                    </Box>}

                    <Divider />
                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <DriverDropdown
                            isFilter={true}
                            selectedDriver={selectedDriver}
                            setSelectedDriver={setSelectedDriver}
                        />
                    </Box>

                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <UserDropdown
                            isFilter={true}
                            selectedUser={selectedUser}
                            setSelectedUser={setSelectedUser}
                        />
                    </Box>
                    <Divider />

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>



                </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'40%'}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                        {allBranchPermission && <Divider orientation="vertical" />}
                        {allBranchPermission && <Box width={'18%'}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>}
                        <Divider orientation="vertical" />
                        <Box width={allBranchPermission ? '18%' : '20%'}>
                            <DriverDropdown
                                isFilter={true}
                                selectedDriver={selectedDriver}
                                setSelectedDriver={setSelectedDriver}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={allBranchPermission ? '18%' : '20%'}>
                            <UserDropdown
                                isFilter={true}
                                selectedUser={selectedUser}
                                setSelectedUser={setSelectedUser}
                            />
                        </Box>
                        <Divider orientation="vertical" />

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            {/* Scrollable Table with Sticky Header */}
            <TableContainer
                component={Paper}
                ref={tableContainerRef}
                onScroll={handleScroll}
                sx={{
                    boxShadow: 'none',
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    borderRadius: 2,
                    height: 'calc(100vh - 226px)',
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader aria-label="simple table">
                    <TableHead
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <TableRow>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}>
                                {t('number')}
                            </TableCell>
                            <TableCell align={isEn ? 'left' : 'right'}
                                onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('id')}
                                    {sortState.sortBy === 'id' && sortIcon()}
                                </Box>
                            </TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('driver')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('driver')}
                                    {sortState.sortBy === 'driver' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('user')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('user')}
                                    {sortState.sortBy === 'user' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('branch')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('branch')}
                                    {sortState.sortBy === 'branch' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('priceAmount')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('priceAmount')}
                                    {sortState.sortBy === 'priceAmount' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('createdAt')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('createdAt')}
                                    {sortState.sortBy === 'createdAt' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {driverPayments.map((payment, index) => (
                            <TableRow
                                key={payment.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{index + 1}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.id}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.driver.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.user.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.branch.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(payment.priceAmount)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{payment.createdAt}</TableCell>

                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        border: 1,
                                        borderColor: theme.palette.customGrey.borderInput,
                                        backgroundColor: theme.palette.secondary.main,
                                        borderRadius: 1,
                                    }}>
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer'
                                        }} onClick={() => handleOpenView(payment)}>
                                            <RemoveRedEyeOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.4,
                                            pb: 0.25,
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            opacity: 1,
                                        }}
                                            component={Link}
                                            to={`/driver-payments/${payment.id}`}
                                        >
                                            <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            opacity: 1,
                                        }} onClick={() => handleOpenDelete(payment)}>
                                            <DeleteOutlineOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.red.bg }}
                                            />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            opacity: 1,
                                        }}>
                                            <PdfOneDriverPayment
                                                id={payment.id}
                                            />
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default DriverPayments;
